import {
  createFileRoute,
  Outlet,
  useRouter,
  redirect,
} from "@tanstack/react-router";
import { ProviderLayout } from "@/components/Layouts/ProviderLayout";
import { useEffect, useState } from "react";
import {
  supabase,
  setStoredSessionTimeoutMinutes,
} from "@/ajax/clients/supabase";
import { queryClient } from "@/ajax/queryClient";
import { hasIncompleteOnboardingSteps } from "@/features/onboarding/utils/onboarding-step-rules";
import OfflineHandler from "@/components/OfflineHandler";
import { z } from "zod";
import { NotFoundComponent } from "@/components/NotFoundComponent";
import { useTRPC, trpc } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import * as Sentry from "@sentry/browser";
import { usePostHog } from "posthog-js/react";
import { Toaster } from "@/components/ui/sonner";
import { TRPCClientError } from "@trpc/client";
import { isNative } from "@/common/utils/environment";
import { PushNotificationService } from "@/common/utils/PushNotificationService";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PushNotificationsPrompt } from "@/components/PushNotifications/PushNotificationsPrompt";
import { getProviderNotificationAudience } from "@/components/PushNotifications/notification-audience";
import { Storage } from "@/common/utils/storage";
import { enforceMfaGate } from "@/features/mfa/enforceMfaGate";
import { isImpersonating } from "@/features/impersonation/impersonation-session";
import { useTimezoneSync } from "@/features/user/hooks/useTimezoneSync";

const PUSH_PERMISSION_DIALOG_KEY = "push_permission_dialog_last_prompt";

export const Route = createFileRoute("/_provider")({
  validateSearch: z.object({
    hideNav: z.boolean().optional(),
    l: z.string().optional(),
  }),
  loader: async ({ location }) => {
    try {
      const user = await queryClient.fetchQuery(
        trpc.user.getUser.queryOptions(undefined, {
          retry: (failureCount, error: unknown) => {
            if (
              failureCount >= 3 ||
              (error as { data?: { code?: string } })?.data?.code ===
                "UNAUTHORIZED"
            ) {
              return false;
            }
            return true;
          },
        }),
      );
      // 2FA gate: if this user must use 2FA and the session's second factor
      // isn't verified yet, redirect to MFA setup before fetching any provider
      // data. The API also enforces this independently.
      await enforceMfaGate(user.mfa_required, location.pathname);

      const providerUser = await queryClient.fetchQuery(
        trpc.provider.getProviderUser.queryOptions(undefined, {
          retry: (failureCount, error: unknown) => {
            if (
              failureCount >= 3 ||
              (error as { data?: { code?: string } })?.data?.code ===
                "UNAUTHORIZED"
            ) {
              return false;
            }
            return true;
          },
        }),
      );

      const isOnboarding = hasIncompleteOnboardingSteps(providerUser);

      if (isOnboarding && location.pathname.startsWith("/provider/")) {
        throw redirect({
          to: "/provider-onboarding",
        });
      }

      return { user, providerUser };
    } catch (error) {
      if (error instanceof TRPCClientError) {
        if (error.data?.code === "UNAUTHORIZED") {
          throw redirect({
            to: "/auth-provider/signin",
            search: {
              redirect: `${location.pathname}${location.searchStr ?? ""}`,
              // @ts-expect-error l exists
              l: location.search?.l,
            },
            reloadDocument: true,
          });
        }
      }
      throw error;
    }
  },
  notFoundComponent: () => <NotFoundComponent homePath="/provider/home" />,
  component: ProviderRouteComponent,
});

function ProviderRouteComponent() {
  const router = useRouter();
  const trpc = useTRPC();
  const posthog = usePostHog();
  const { user, providerUser: loaderProviderUser } = Route.useLoaderData();
  const [showPushDialog, setShowPushDialog] = useState(false);
  const { data: providerUser } = useQuery({
    ...trpc.provider.getProviderUser.queryOptions(),
    initialData: loaderProviderUser,
  });

  useTimezoneSync(user != null);

  useEffect(() => {
    supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_OUT") {
        queryClient.clear();
        router.navigate({
          to: "/auth-provider/signin",
          search: {
            redirect: `${router.state.location.pathname}${router.state.location.searchStr ?? ""}`,
          },
          reloadDocument: true,
        });
      }
    });
  }, [router]);

  useEffect(() => {
    // While impersonating, analytics, error reports, and the idle timeout
    // stay tied to the staff member's own sign-in.
    if (isImpersonating()) {
      return;
    }
    if (user) {
      Sentry.setUser({
        id: user?.id,
      });
      posthog.identify(user?.id);
      void setStoredSessionTimeoutMinutes(user.session_timeout_minutes ?? null);
    } else {
      Sentry.setUser(null);
      posthog.reset();
    }
  }, [posthog, user]);

  useEffect(() => {
    if (!providerUser || !isNative()) {
      return;
    }
    const hasCompletedOnboarding = !hasIncompleteOnboardingSteps(providerUser);
    if (!hasCompletedOnboarding) {
      return;
    }
    const checkPermission = async () => {
      await PushNotificationService.checkPermissionOncePerDay();
      const status = await PushNotificationService.checkPermissionStatus();
      if (status?.receive !== "granted") {
        const today = new Date().toISOString().split("T")[0];
        const lastPrompt = await Storage.getItem(PUSH_PERMISSION_DIALOG_KEY);
        if (lastPrompt !== today) {
          setShowPushDialog(true);
          await Storage.setItem(PUSH_PERMISSION_DIALOG_KEY, today);
        }
      } else {
        await PushNotificationService.registerDeviceToken();
        setShowPushDialog(false);
      }
    };
    checkPermission();
    const interval = setInterval(
      () => {
        void checkPermission();
      },
      24 * 60 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, [providerUser]);

  const handlePushDialogComplete = async (success: boolean) => {
    setShowPushDialog(false);
    if (success) {
      await PushNotificationService.checkPermissionOncePerDay();
    } else {
      const today = new Date().toISOString().split("T")[0];
      await Storage.setItem(PUSH_PERMISSION_DIALOG_KEY, today);
    }
  };

  const handleDialogOpenChange = (open: boolean) => {
    setShowPushDialog(open);
    if (!open) {
      const today = new Date().toISOString().split("T")[0];
      void Storage.setItem(PUSH_PERMISSION_DIALOG_KEY, today);
    }
  };

  if (!user || !providerUser) {
    return <OfflineHandler homePath="/provider/home" />;
  }

  const isOnboarding = hasIncompleteOnboardingSteps(providerUser);

  return (
    <ProviderLayout
      user={user}
      showSidebar={!isOnboarding}
      jobPostsEnabled={providerUser.jobPostsEnabled}
    >
      <Outlet />
      <Toaster />
      <Dialog open={showPushDialog} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="max-w-xl">
          <DialogTitle className="sr-only">Stay in the loop</DialogTitle>
          <PushNotificationsPrompt
            onComplete={handlePushDialogComplete}
            audience={getProviderNotificationAudience(
              providerUser.jobPostsEnabled,
            )}
            showHeading
            title="Stay in the loop"
          />
        </DialogContent>
      </Dialog>
    </ProviderLayout>
  );
}
